import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, FileText, Clock, TrendingUp, AlertCircle, Settings,
  Search, Filter, Download, Mail, RefreshCw, Printer, QrCode,
  Ban, ChevronLeft, ChevronRight, CheckCircle, ExternalLink
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VeriFactuDashboard() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const shopId = user?.shop_id;

  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anularDialog, setAnularDialog] = useState(null);
  const [anularReason, setAnularReason] = useState('');
  const [anularLoading, setAnularLoading] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStatus = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await fetch(`${API}/api/verifactu/status/${shopId}`, { headers });
      const data = await res.json();
      setStatus(data);
    } catch (e) { /* silent */ }
  }, [shopId, token]);

  const fetchInvoices = useCallback(async () => {
    if (!shopId) return;
    try {
      let url = `${API}/api/verifactu/invoices/${shopId}?page=${page}&limit=15`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setInvoices(data.invoices || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (e) { /* silent */ }
  }, [shopId, token, page, statusFilter]);

  const fetchErrors = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await fetch(`${API}/api/verifactu/errors/${shopId}`, { headers });
      const data = await res.json();
      setErrors(Array.isArray(data) ? data.slice(0, 3) : []);
    } catch (e) { /* silent */ }
  }, [shopId, token]);

  useEffect(() => {
    Promise.all([fetchStatus(), fetchInvoices(), fetchErrors()]).finally(() => setLoading(false));
  }, [fetchStatus, fetchInvoices, fetchErrors]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchStatus(), fetchInvoices(), fetchErrors()]).finally(() => setLoading(false));
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/api/verifactu/invoices/${shopId}/export/csv`, { headers });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verifactu_invoices.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const handleAnular = async () => {
    if (!anularReason.trim()) { toast.error('Reason is required'); return; }
    setAnularLoading(true);
    try {
      const res = await fetch(`${API}/api/verifactu/invoices/${shopId}/anular`, {
        method: 'POST', headers,
        body: JSON.stringify({ invoice_id: anularDialog.id, reason: anularReason })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success('Invoice cancelled successfully');
      setAnularDialog(null);
      setAnularReason('');
      fetchInvoices();
      fetchStatus();
    } catch (e) {
      toast.error(e.message || 'Failed to cancel');
    } finally {
      setAnularLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim() || !emailTo.includes('@')) { toast.error('Valid email required'); return; }
    setEmailLoading(true);
    try {
      const res = await fetch(`${API}/api/verifactu/invoices/${shopId}/${emailDialog.id}/send-email`, {
        method: 'POST', headers,
        body: JSON.stringify({ to_email: emailTo })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success(`Invoice emailed to ${emailTo}`);
      setEmailDialog(null);
      setEmailTo('');
      fetchInvoices();
    } catch (e) {
      toast.error(e.message || 'Failed to send email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePrint = (inv) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${inv.invoice_series}-${String(inv.invoice_number).padStart(5,'0')}</title>
    <style>body{font-family:Arial,sans-serif;padding:32px;max-width:700px;margin:0 auto}
    .header{border-bottom:2px solid #1e3a5f;padding-bottom:12px;margin-bottom:20px}
    .header h1{color:#1e3a5f;font-size:20px;margin:0}
    .header p{color:#666;font-size:12px;margin:4px 0 0}
    table{width:100%;border-collapse:collapse;margin:16px 0}
    th{background:#f0f4f8;text-align:left;padding:8px;font-size:12px;border:1px solid #ddd}
    td{padding:8px;font-size:12px;border:1px solid #ddd}
    .total-row{background:#fffbeb}
    .total-row td{font-weight:bold}
    .hash{font-family:monospace;font-size:10px;color:#888;word-break:break-all;margin-top:16px}
    .footer{border-top:1px solid #ddd;padding-top:12px;margin-top:20px;font-size:10px;color:#999}
    @media print{body{padding:16px}}</style></head><body>
    <div class="header">
      <h1>${inv.razon_social || 'Business Name'}</h1>
      <p>NIF: ${inv.nif_emisor} | ${inv.direccion_fiscal || ''}, ${inv.ciudad || ''} ${inv.codigo_postal || ''}</p>
      <p style="float:right;margin-top:-32px;font-size:14px;font-weight:bold">FACTURA ${inv.invoice_series}-${String(inv.invoice_number).padStart(5,'0')}</p>
    </div>
    <table>
      <tr><th>Field</th><th>Value</th></tr>
      <tr><td>Invoice Number</td><td>${inv.invoice_series}-${String(inv.invoice_number).padStart(5,'0')}</td></tr>
      <tr><td>Date</td><td>${inv.invoice_date}</td></tr>
      <tr><td>Type</td><td>${inv.tipo_factura}</td></tr>
      ${inv.nif_destinatario ? `<tr><td>Customer NIF</td><td>${inv.nif_destinatario}</td></tr>` : ''}
      ${inv.nombre_destinatario ? `<tr><td>Customer</td><td>${inv.nombre_destinatario}</td></tr>` : ''}
      ${(inv.iva_breakdown || []).map(b => `<tr><td>Base (${b.rate}%)</td><td>${b.base.toFixed(2)} EUR</td></tr><tr><td>IVA ${b.rate}%</td><td>${b.cuota.toFixed(2)} EUR</td></tr>`).join('')}
      <tr class="total-row"><td>TOTAL</td><td>${inv.total.toFixed(2)} EUR</td></tr>
    </table>
    <div class="hash">Hash: ${inv.huella_hash || ''}</div>
    ${inv.qr_verification_url ? `<p style="font-size:11px"><a href="${inv.qr_verification_url}">AEAT Verification</a></p>` : ''}
    <div class="footer">VERI*FACTU - Factura verificable en sede.agenciatributaria.gob.es | Generated by VendFactura</div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  const getStatusBadge = (inv) => {
    if (inv.is_anulada) return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">ANULADA</span>;
    if (inv.tipo_factura === 'ANULACION') return <span className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-600 text-xs font-medium rounded-full">ANULACION</span>;
    if (inv.is_rectificativa) return <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-600 text-xs font-medium rounded-full">RECTIFICATIVA</span>;

    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[inv.aeat_status] || 'bg-slate-100 text-slate-600'}`}>
        {inv.aeat_status?.toUpperCase()}
      </span>
    );
  };

  const getTypeBadge = (tipo) => {
    const colors = {
      'F1': 'bg-blue-50 text-blue-700 border-blue-200',
      'F2': 'bg-slate-50 text-slate-700 border-slate-200',
      'ANULACION': 'bg-red-50 text-red-700 border-red-200',
    };
    const style = colors[tipo] || 'bg-purple-50 text-purple-700 border-purple-200';
    return <span className={`px-1.5 py-0.5 text-xs font-mono font-medium rounded border ${style}`}>{tipo}</span>;
  };

  if (loading && !status) return (
    <ShopLayout>
      <div className="flex items-center justify-center h-64" data-testid="verifactu-dashboard-loading">
        <div className="w-8 h-8 border-4 border-[#1e3a5f]/20 border-t-[#1e3a5f] rounded-full animate-spin" />
      </div>
    </ShopLayout>
  );

  if (!status?.enabled) return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center" data-testid="verifactu-not-enabled">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 max-w-lg mx-auto">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">VeriFactu Not Configured</h2>
          <p className="text-sm text-slate-600 mb-4">Enable VeriFactu in settings to start generating fiscal invoices.</p>
          <button onClick={() => navigate('/shop/verifactu/settings')}
            className="px-5 py-2.5 bg-[#f97316] hover:bg-[#ea580c] text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="go-to-settings-btn"
          >
            <Settings className="w-4 h-4 inline mr-2" /> Go to Settings
          </button>
        </div>
      </div>
    </ShopLayout>
  );

  return (
    <ShopLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="verifactu-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#f97316]" />
            <h1 className="text-2xl font-bold text-[#1e3a5f]" data-testid="verifactu-dashboard-title">VeriFactu Invoices</h1>
          </div>
          <button onClick={() => navigate('/shop/verifactu/settings')}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" /> Settings
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Invoices', value: status.total_invoices, icon: FileText, color: 'text-[#1e3a5f]', bg: 'bg-blue-50' },
            { label: 'Pending', value: status.pending_submissions, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
            { label: 'Success Rate 7d', value: `${status.success_rate_7d}%`, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Errors Today', value: status.errors_today, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-lg p-4"
              data-testid={`stat-card-${i}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 font-medium">{stat.label}</span>
                <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Chain Integrity */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-testid="chain-integrity-card">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Chain Integrity:</span>
            {status.chain_integrity === 'valid' ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                <CheckCircle className="w-3 h-3" /> VALID
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                <AlertCircle className="w-3 h-3" /> CHECK REQUIRED
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span>Next: <span className="font-mono text-slate-700">{status.next_invoice_number}</span></span>
            {status.last_accepted_at && (
              <span>Last accepted: {new Date(status.last_accepted_at).toLocaleString()}</span>
            )}
          </div>
        </div>

        {/* Errors Alert */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="errors-alert">
            <p className="font-semibold text-red-800 text-sm mb-2">Recent Errors</p>
            {errors.map((err, i) => (
              <p key={i} className="text-xs text-red-700 mb-1">
                [{err.error_code}] {err.error_message} — {err.recommended_action}
              </p>
            ))}
          </div>
        )}

        {/* Invoices Table */}
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="invoices-table-card">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search invoices..."
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                  data-testid="search-input"
                />
              </div>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                data-testid="status-filter"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleRefresh} className="p-2 hover:bg-slate-100 rounded-lg" title="Refresh" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 text-slate-500" />
              </button>
              <button onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" /> CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Invoice</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Base</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">IVA</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                      No fiscal invoices yet. Invoices will appear here when orders are completed.
                    </td>
                  </tr>
                ) : invoices.filter(inv => {
                  if (!search) return true;
                  const q = search.toLowerCase();
                  return String(inv.invoice_number).includes(q) ||
                    inv.invoice_series?.toLowerCase().includes(q) ||
                    inv.nombre_destinatario?.toLowerCase().includes(q) ||
                    inv.nif_destinatario?.toLowerCase().includes(q);
                }).map(inv => (
                  <tr key={inv.id} className="hover:bg-slate-50/50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono text-sm font-medium text-[#1e3a5f]">
                      {inv.invoice_series}-{String(inv.invoice_number).padStart(5, '0')}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{inv.invoice_date}</td>
                    <td className="px-4 py-3">{getTypeBadge(inv.tipo_factura)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{inv.base_imponible?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{inv.cuota_iva?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1e3a5f]">{inv.total?.toFixed(2)} &euro;</td>
                    <td className="px-4 py-3">{getStatusBadge(inv)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handlePrint(inv)} className="p-1.5 hover:bg-slate-100 rounded" title="Print"
                          data-testid={`print-btn-${inv.id}`}
                        >
                          <Printer className="w-4 h-4 text-slate-500" />
                        </button>
                        {inv.qr_verification_url && (
                          <a href={inv.qr_verification_url} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 hover:bg-slate-100 rounded" title="Verify QR"
                          >
                            <QrCode className="w-4 h-4 text-slate-500" />
                          </a>
                        )}
                        <button
                          onClick={() => { setEmailDialog(inv); setEmailTo(inv.nombre_destinatario ? '' : ''); }}
                          className="p-1.5 hover:bg-blue-50 rounded" title="Send Email"
                          data-testid={`email-btn-${inv.id}`}
                        >
                          <Mail className={`w-4 h-4 ${inv.email_sent_at ? 'text-green-500' : 'text-slate-500'}`} />
                        </button>
                        {!inv.is_anulada && inv.tipo_factura !== 'ANULACION' && (
                          <button onClick={() => setAnularDialog(inv)} className="p-1.5 hover:bg-red-50 rounded" title="Cancel Invoice"
                            data-testid={`anular-btn-${inv.id}`}
                          >
                            <Ban className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-100 flex items-center justify-between" data-testid="pagination">
              <span className="text-xs text-slate-500">{total} invoices total</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1.5 border border-slate-200 rounded text-sm disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-slate-200 rounded text-sm disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Anular Dialog */}
      {anularDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="anular-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAnularDialog(null)} />
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Ban className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-bold text-[#1e3a5f]">Cancel Invoice</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Cancel invoice <span className="font-mono font-medium">{anularDialog.invoice_series}-{String(anularDialog.invoice_number).padStart(5, '0')}</span>?
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-sm text-slate-600">
              <p>Total: <span className="font-semibold">{anularDialog.total?.toFixed(2)} EUR</span></p>
              <p>Date: {anularDialog.invoice_date}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason for cancellation *</label>
              <input
                value={anularReason} onChange={e => setAnularReason(e.target.value)}
                placeholder="Enter reason..."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                data-testid="anular-reason-input"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">This action cannot be undone. An ANULACION record will be created in the hash chain.</p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => { setAnularDialog(null); setAnularReason(''); }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
                data-testid="anular-cancel-btn"
              >
                Cancel
              </button>
              <button onClick={handleAnular} disabled={anularLoading || !anularReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="anular-confirm-btn"
              >
                {anularLoading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Email Dialog */}
      {emailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="email-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEmailDialog(null)} />
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-[#1e3a5f]" />
              <h3 className="text-lg font-bold text-[#1e3a5f]">Send Invoice Email</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Send fiscal invoice <span className="font-mono font-medium">{emailDialog.invoice_series}-{String(emailDialog.invoice_number).padStart(5, '0')}</span> to customer via email.
            </p>

            {emailDialog.email_sent_at && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-green-700">
                  Previously sent to {emailDialog.email_sent_to} on {new Date(emailDialog.email_sent_at).toLocaleString()}
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Email *</label>
              <input
                type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                placeholder="customer@example.com" autoFocus
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                data-testid="email-to-input"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-sm text-slate-600">
              <p>Total: <span className="font-semibold">{emailDialog.total?.toFixed(2)} EUR</span></p>
              <p>Date: {emailDialog.invoice_date}</p>
              <p className="text-xs text-slate-400 mt-1">Uses your custom VeriFactu email template if configured</p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => { setEmailDialog(null); setEmailTo(''); }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
                data-testid="email-cancel-btn"
              >
                Cancel
              </button>
              <button onClick={handleSendEmail} disabled={emailLoading || !emailTo.includes('@')}
                className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#2a4d73] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="email-send-btn"
              >
                {emailLoading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ShopLayout>
  );
}
